import { AlertOctagon, Copy, CheckCircle2 } from "lucide-react";
import { Modal } from "../Modal";
import { ActionButton, GhostButton, FormField, inputClass } from "../Primitives";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import SearchableSelect from "../SearchableSelect";
import { ShiftTZPreview } from "./ShiftTZPreview";
import DurationChips from "./DurationChips";
import { Link } from "react-router-dom";

export function EditShiftModal({
  editShift,
  form,
  setForm,
  guards,
  guardOptions,
  sites,
  isConflict,
  onClose,
  onSave,
  onDelete,
  onDuplicate,
  submitting = false,
}) {
  const inactiveOnShift = editShift.inactive_guard_ids && editShift.inactive_guard_ids.length > 0;
  const selectedSite = form.site_id ? sites.find((s) => s.id === form.site_id) : null;
  // iter302 — Completed shifts open as VIEW-ONLY. Once a guard has clocked
  // in AND out, edits belong on the Attendance page (which owns the source
  // of truth for hours worked). Editing the roster shift's start/end/site
  // after clock-out would leave the attendance record misaligned with the
  // schedule and confuse payroll. Deep-link straight to /attendance with
  // the shift preselected so the operator's flow isn't interrupted.
  const isCompleted = editShift.status === "completed";
  const attendanceHref = `/attendance?shift=${encodeURIComponent(editShift.id)}`;
  return (
    <Modal title={isCompleted ? "Completed Shift · View Only" : "Edit Shift"} onClose={onClose} testId="edit-shift-modal">
      {isCompleted && (
        <div
          className="mb-4 border border-[#30D158]/50 bg-[#30D158]/10 p-3 flex items-start gap-2"
          data-testid="completed-banner"
        >
          <CheckCircle2 className="size-4 text-[#30D158] shrink-0 mt-0.5" />
          <div className="text-xs flex-1">
            <div className="font-bold text-[#30D158] uppercase tracking-widest mb-0.5">Shift completed</div>
            <div className="text-zinc-400">
              This shift has been clocked out. To correct hours, notes, or clock-in / clock-out times, edit the{" "}
              <Link
                to={attendanceHref}
                data-testid="completed-edit-attendance-link"
                className="text-[#0A84FF] underline hover:text-white"
                onClick={onClose}
              >
                attendance record
              </Link>{" "}
              instead. Roster-side edits are disabled to keep payroll and the schedule in sync.
            </div>
          </div>
        </div>
      )}      {isConflict && (
        <div className="mb-4 border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-3 flex items-start gap-2" data-testid="conflict-banner">
          <AlertOctagon className="size-4 text-[#FF3B30] shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold text-[#FF3B30] uppercase tracking-widest mb-0.5">Schedule conflict</div>
            <div className="text-zinc-400">This  is double-booked. Change the officer, site, or time to resolve.</div>
          </div>
        </div>
      )}
      {inactiveOnShift && (
        <div className="mb-4 border border-[#FF3B30]/50 bg-[#FF3B30]/20 p-3 flex items-start gap-2" data-testid="inactive-banner">
          <AlertOctagon className="size-4 text-[#FF3B30] shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold text-[#FF3B30] uppercase tracking-widest mb-0.5">Inactive  on shift</div>
            <div className="text-zinc-400">
              {editShift.inactive_guard_ids.map((gid) => {
                const g = guards.find((x) => x.id === gid);
                return g ? `${g.name} (deactivated ${g.deactivation_date || ""})` : gid;
              }).join(", ")}
              . Reassign or remove this guard.
            </div>
          </div>
        </div>
      )}
      {editShift.is_open && (
        <div className="mb-4 border border-[#FF9F0A]/50 bg-[#FF9F0A]/10 p-3 flex items-start gap-2" data-testid="open-banner">
          <AlertOctagon className="size-4 text-[#FF9F0A] shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold text-[#FF9F0A] uppercase tracking-widest mb-0.5">Open shift</div>
            <div className="text-zinc-400">No  assigned. Pick one or more officers below, or save as-is to keep it open.</div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <FormField label="Officers (one or more)">
            <MultiSelectDropdown
              testId="edit-shift-guards"
              label="Select "
              options={guardOptions}
              selected={form.guard_ids}
              onChange={(ids) => setForm({ ...form, guard_ids: ids })}
              searchable
              triggerClassName="w-full"
              emptyHint="No  match"
              disabled={isCompleted}
            />
          </FormField>
        </div>
        <FormField label="Site">
          {/* iter307 — Same searchable select used in the create modal. */}
          <SearchableSelect
            testId="edit-shift-site"
            options={sites.map((s) => ({ value: s.id, label: s.name, hint: s.address || undefined }))}
            value={form.site_id || ""}
            onChange={(v) => setForm({ ...form, site_id: v })}
            placeholder="Select site…"
            emptyHint="No sites match"
            disabled={isCompleted}
            allowClear={false}
          />
        </FormField>
        <div></div>
        <FormField label="Start">
          <input type="datetime-local" data-testid="edit-shift-start" className={inputClass} style={{ colorScheme: "dark" }} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} disabled={isCompleted} />
        </FormField>
        <FormField label="End">
          <input type="datetime-local" data-testid="edit-shift-end" className={inputClass} style={{ colorScheme: "dark" }} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} disabled={isCompleted} />
          {/* iter313 — same one-tap presets as the create modal. */}
          <DurationChips
            startLocal={form.start_time}
            endLocal={form.end_time}
            onPick={(nextEnd) => setForm({ ...form, end_time: nextEnd })}
            disabled={isCompleted}
            testIdPrefix="edit-shift-duration"
          />
        </FormField>

        {/* iter289 — Same site-local TZ preview used by ShiftFormModal.
            Prevents drift when an ops user retimes a single shift for a
            site in a different zone. */}
        <ShiftTZPreview
          site={selectedSite}
          startLocal={form.start_time}
          endLocal={form.end_time}
          testIdPrefix="edit-shift-tz-preview"
        />

        <FormField label="Minimum Officers Required">
          <input
            type="number"
            min="0"
            max="20"
            data-testid="edit-shift-min-guards"
            className={inputClass}
            value={form.min_guards}
            onChange={(e) => setForm({ ...form, min_guards: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })}
            disabled={isCompleted}
          />
        </FormField>
        <div></div>
        <div className="col-span-2">
          <FormField label="Notes">
            <textarea rows={2} className={inputClass} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={isCompleted} />
          </FormField>
        </div>
      </div>
      {editShift.recurrence_group && (
        <div className="text-[10px] text-[#FF9F0A] uppercase tracking-widest mt-3">
          ↻ Belongs to a series. Changes here affect only this date — use the series icon (✎) to edit all.
        </div>
      )}
      <div className="mt-6 flex justify-between items-center gap-2">
        {isCompleted ? (
          <>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
              Completed — no roster-side edits
            </div>
            <div className="flex gap-2">
              <GhostButton onClick={onClose} data-testid="close-completed-shift-button">Close</GhostButton>
              <Link
                to={attendanceHref}
                onClick={onClose}
                data-testid="open-attendance-record-button"
                className="inline-flex items-center gap-1.5 border border-[#0A84FF] bg-[#0A84FF]/10 hover:bg-[#0A84FF]/20 text-[#0A84FF] px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors"
              >
                Open Attendance Record →
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <button
                data-testid="delete-shift-button"
                onClick={() => onDelete(editShift.id)}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
              >
                Delete
              </button>
              <button
                data-testid="duplicate-shift-button"
                onClick={() => onDuplicate(editShift)}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#0A84FF] inline-flex items-center gap-1"
              >
                <Copy className="size-3" /> Duplicate
              </button>
            </div>
            <div className="flex gap-2">
              <GhostButton onClick={onClose}>Cancel</GhostButton>
              <ActionButton data-testid="save-edit-shift-button" onClick={onSave} disabled={submitting}>{submitting ? "Saving…" : "Save"}</ActionButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
